import ApiResponseV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiResponseV2";
import { IPageV2 } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/IPageV2";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import GenericCrudServiceV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/GenericCrudServiceV2";
import HttpClientV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/HttpClientV2";
import { HelpMeWriteMessage } from "@/models/ai-chat/HelpMeWriteMessage";
import { LazoChatMessage } from "@/models/ai-chat/LazoChatMessage";
import environment from "../../environment.json";

class LazoChatMessageService extends GenericCrudServiceV2<LazoChatMessage> {
  path: string;
  constructor(path: string) {
    super(path);
    this.path = path;
  }

  createNewMessage(
    message: string,
    conversationExternalId: string,
    messageReceiverIsBot: boolean,
    signal?: AbortSignal
  ): Promise<ApiResponseV2<LazoChatMessage>> {
    // We add timeout because chatGPT takes too long to respond, and even if the backend doesn't timeout, the frontend does
    return HttpClientV2.post(
      `${this.path}`,
      { conversationExternalId, message, messageReceiverIsBot },
      { axiosConfig: { timeout: 2400000, signal } }
    );
  }

  sendHiddenMessage(message: string, conversationExternalId: string, signal?: AbortSignal): Promise<ApiResponseV2<LazoChatMessage>> {
    // We add timeout because chatGPT takes too long to respond, and even if the backend doesn't timeout, the frontend does
    return HttpClientV2.post(`${this.path}/hidden`, { conversationExternalId, message }, { axiosConfig: { timeout: 2400000, signal } });
  }

  helpMeWriteMessage(helpMeWriteMessage: HelpMeWriteMessage, signal?: AbortSignal): Promise<ApiResponseV2<HelpMeWriteMessage>> {
    // We add timeout because chatGPT takes too long to respond, and even if the backend doesn't timeout, the frontend does
    return HttpClientV2.post(`${this.path}/help-me-write`, helpMeWriteMessage, {
      axiosConfig: { timeout: 2400000, signal },
    });
  }

  sendAttachments(externalId: string, files: File[]): Promise<ApiResponseV2<LazoChatMessage>> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append(`files`, file, file.name);
    });
    return HttpClientV2.post(`${this.path}/attachments?conversationExternalId=${externalId}`, formData, {
      axiosConfig: {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    });
  }

  sendStreamedMessage(
    message: string,
    conversationExternalId: string,
    handleToken: (s: string) => void,
    handleCompletion?: () => void
  ): any {
    const body = {
      message,
      conversationExternalId,
      messageReceiverIsBot: true,
    };

    // We have to use Fetch API here since Axios do not support POST streamings.
    fetch(`${environment.baseURL}${this.basePath}/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + AuthService.getToken(),
      },
      body: JSON.stringify(body),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response?.body?.getReader();
      const readChunk = () => {
        reader?.read().then(({ value, done }) => {
          if (done) {
            handleCompletion?.();
            return;
          }

          handleToken(new TextDecoder().decode(value));
          readChunk();
        });
      };

      readChunk();
    });
  }

  search(conversationId: string, query: string | null, date: Date | null): Promise<ApiResponseV2<string[]>> {
    return HttpClientV2.post(`${this.path}/search?conversationId=${conversationId}&query=${query}&date=${date}`, {});
  }

  // This endpoint is used to send a Typing Event to the backend, and notify all users that the user is typing on the chat
  sendTypingEvent(conversationId: string, isTyping: boolean): Promise<ApiResponseV2<string>> {
    return HttpClientV2.post(`${this.path}/user-typing?conversationExternalId=${conversationId}&isTyping=${isTyping}`, {});
  }

  findByConversationExternalIdPaged(conversationExternalId: string, page: number): Promise<ApiResponseV2<IPageV2<LazoChatMessage>>> {
    const params = new URLSearchParams({
      conversationExternalId,
      page: page.toString(),
    });

    return HttpClientV2.get(`${this.path}/conversation`, {
      axiosConfig: {
        timeout: 15000,
        params,
      },
    });
  }
}

export default new LazoChatMessageService("/api/lazo-chat-message");
